import React, { Component } from 'react';
import PropTypes from 'prop-types';
import turf from '@turf/turf';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { assign, find, get, hasIn, includes, isEmpty, isNil, keys, map, toNumber } from 'lodash';
import { parse } from 'qs';
import { withTracker } from 'meteor/react-meteor-data';
import { Bert } from 'meteor/themeteorchef:bert';
import * as topojson from 'topojson-client';
import { Meteor } from 'meteor/meteor';
import Layers, {
  GEOJSON_GEOMETRIES,
  LAYER_SUBTYPES,
  LAYER_TYPES,
  LAYER_TYPES_FREE,
  getSubTypesForType,
  FREE_SUBTYPES
} from '../../../api/Layers/Layers';
import * as layerOps from '../../../api/Layers/layer-operations';

import adaptURL from '../../../modules/adapt-url';
import { coordinatesToXYZ } from '../../../modules/coordinates-to-xyz';
import getGeoJSONFromCSV from '../../../modules/get-geojson-from-csv';
import getGeoJSONFromExcel from '../../../modules/get-geojson-from-excel';
import getGeoJSONFromMapBox from '../../../modules/get-geojson-from-mapbox';
import getGeoJSONURLFromMapbox from '../../../modules/get-geojson-url-from-mapbox';
import getWMSLayerNamesFromURL from '../../../modules/get-wms-layer-names-from-url';
import getWMSStyleNamesFromURL from '../../../modules/get-wms-style-names-from-url';
import getWMTSParamsFromURL from '../../../modules/get-wmts-params-from-url';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import joinGeoJSONThematicLayer from '../../../modules/join-geojson-thematic-layers';
import loadCSVTable from '../../../modules/load-csv-table';
import loadExcelColumnsAndRows from '../../../modules/load-excel-columns-and-rows';
import loadExcelFromURL from '../../../modules/load-excel-from-url';
import loadExcelSheets from '../../../modules/load-excel-sheets';
import loadGeoJSONFromURL from '../../../modules/load-geojson-from-url';
import loadGeoJSONIndoorFromURL from '../../../modules/load-geojson-indoor-from-url';
import loadMapBoxLayerNames from '../../../modules/load-mapbox-layer-names';
import methodPromise from '../../../modules/method-promise-wrapper';
import { parseGeoJSONProperties } from '../../../modules/parse-geojson-properties';

import LayerFields from '../LayerFields';
import LayerSubTypeField from '../../atoms/LayerSubTypeField';
import Validation from '../../containers/Validation';

const FAILED_FETCH_MESSAGE = 'Failed to fetch';
const LAYERS_URL_PARAM_REGEX = new RegExp('layers', 'i');
const STYLES_URL_PARAM_REGEX = new RegExp('STYLES', 'i');
const LAYER_URL_PARAM_REGEX = new RegExp('layer', 'i');
const STYLE_URL_PARAM_REGEX = new RegExp('style', 'i');
const TILEMATRIXSET_URL_PARAM_REGEX = new RegExp('tilematrixset', 'i');
const FORMAT_URL_PARAM_REGEX = new RegExp('format', 'i');
const NEGATIVE_Y_PARAM_REGEX = new RegExp(/{-y}/);
const NO_TOKEN = 'no-token';

const LOADING_SUBTYPES = [
  LAYER_SUBTYPES.GEOJSON,
  LAYER_SUBTYPES.GEOJSONVT,
  LAYER_SUBTYPES.INDOOR,
  LAYER_SUBTYPES.OGR,
  LAYER_SUBTYPES.REALTIME,
  LAYER_SUBTYPES.TOPOJSON,
  LAYER_SUBTYPES.CSV,
  LAYER_SUBTYPES.EXCEL,
  LAYER_SUBTYPES.FIWARE,
  LAYER_SUBTYPES.GOOGLE_SHEETS,
  LAYER_SUBTYPES.POSTGRESQL,
  LAYER_SUBTYPES.INNERJOIN,
  LAYER_SUBTYPES.LEFTJOIN,
  LAYER_SUBTYPES.RIGHTJOIN
];

const layerNotSupported = (layerType, layerSubType) =>
  includes([LAYER_TYPES.THEMATIC, LAYER_TYPES.SPATIAL], layerType) &&
  includes(
    [LAYER_SUBTYPES.EXTERNAL_SERVER, LAYER_SUBTYPES.FIWARE, LAYER_SUBTYPES.UPLOADED_FILE],
    layerSubType
  );

class LayerEditor extends Component {
  state = {
    annotationLayerProperties: layerOps.getAnnotationLayerProperties(this.props.layer),
    bottomLeft: layerOps.getBottomLeft(this.props.layer),
    defaultUrl: '',
    excelColumns: layerOps.getExcelColumns(this.props.layer),
    excelSheet: layerOps.getExcelSheet(this.props.layer),
    excelTableLength: 1,
    excelTableWidth: 1,
    failedFetchDueToCORS: false,
    fiwareLayerName: layerOps.getFiwareLayerName(this.props.layer),
    fiwareLayers: [layerOps.getFiwareLayerName(this.props.layer)],
    fiwareVersion: layerOps.getFiwareVersion(this.props.layer),
    geojsonLoaded: isEmpty(layerOps.getId(this.props.layer))
      ? !includes(LOADING_SUBTYPES, layerOps.getSubType(this.props.layer))
      : true,
    geojsonLoading: false,
    geojsonProps: layerOps.getGeoJSONProps(this.props.layer),
    georefType: layerOps.getGeorefType(this.props.layer),
    isFeature: layerOps.getIsFeature(this.props.layer),
    bBoxUpdate: false,
    isExternal: false,
    isModal: false,
    layerGeometry: layerOps.getGeoJSONGeometry(this.props.layer),
    layerSubType: layerOps.getSubType(this.props.layer),
    layerSubTypes: this.props.isFree ? FREE_SUBTYPES : LAYER_SUBTYPES,
    layerType: layerOps.getType(this.props.layer),
    layerTypes: this.props.isFree ? LAYER_TYPES_FREE : LAYER_TYPES,
    mapboxLayerName: layerOps.getMapBoxLayerName(this.props.layer),
    mapboxLayerNames: [],
    mapLocation: {},
    ogrLayers: {},
    preselectedStyles: layerOps.getWMSLayerStyle(this.props.layer).split(','),
    sheetList: isEmpty(layerOps.getExcelSheet(this.props.layer))
      ? []
      : [layerOps.getExcelSheet(this.props.layer)],
    showStylesField: false,
    thematicKeyList: layerOps.getThematicKeyList(this.props.layer),
    topLeft: layerOps.getTopLeft(this.props.layer),
    topRight: layerOps.getTopRight(this.props.layer),
    urlIncludesLayers: false,
    urlIncludesWMTSOptions: false,
    wmsLayerTitles: layerOps.getWMSLayerTitles(this.props.layer),
    wmsLayerNames: layerOps.getWMSLayerNames(this.props.layer),
    wmtsFormatId: layerOps.getWMTSFormats(this.props.layer),
    wmtsLayerId: layerOps.getWMTSLayer(this.props.layer),
    wmtsLayerIds: layerOps.getWMTSLayer(this.props.layer),
    wmtsStyleId: layerOps.getWMTSStyle(this.props.layer),
    wmtsTileMatrixSetId: layerOps.getWMTSTileMatrixSetNames(this.props.layer)
  };

  componentDidMount() {
    const dat = localStorage.getItem('Metadata');
    const data = JSON.parse(dat);
    if (!isEmpty(data)) {
      this.setState({ defaultUrl: data.url, isModal: data.isModal, isFeature: data.isFeature });
      const event = { target: { value: [data.subtype] } };
      event.target.value = data.subtype;
      this.handleLayerSubTypeChange(event);
      // const eventURL = { target: { form: { url: { value: data.url } } } };
      const searchRegex = new RegExp('bbox', 'i');

      if (searchRegex.test(data.url)) {
        const spl1 = data.url.split('bbox=', 2);
        const spl2 = spl1[1].split('&', 1);
        const spl3 = spl2[0].split(',');
        const bbox = spl3.map(Number);
        const incrX = bbox[2] - bbox[0];
        const incrY = bbox[3] - bbox[1];
        const Cx = (bbox[2] + bbox[0]) / 2;
        const Cy = (bbox[3] + bbox[1]) / 2;
        const maxDiff = incrX > incrY ? incrX : incrY;
        const zoomLevel = Math.round(
          -1 * (Math.log(maxDiff) / Math.log(2) - Math.log(360) / Math.log(2))
        );
        const MapLocation = {
          type: 'Feature',
          properties: {
            zoom: zoomLevel
          },
          geometry: {
            type: 'Point',
            coordinates: [Cx, Cy]
          },
          bbox
        };
        this.setState({ bBoxUpdate: true, mapLocation: MapLocation });
      }

      // this.loadGeoJSON(eventURL);

      localStorage.removeItem('Metadata');
    }
  }

  changeLayerEditorStateProperty = (property, newValue) => this.setState({ [property]: newValue });

  handleLayerSubTypeChange = event => {
    const newLayerSubType = event.target.value.trim();
    const { layer } = this.props;
    const existingLayer = !isEmpty(layerOps.getId(this.props.layer));
    if (
      !includes(LOADING_SUBTYPES, newLayerSubType) ||
      (existingLayer && includes(LOADING_SUBTYPES, layerOps.getSubType(layer)))
    ) {
      this.setState({ geojsonLoaded: true });
    } else {
      this.setState({ geojsonLoaded: false });
    }
    this.setState({ layerSubType: newLayerSubType, isExternal: false });
  };

  handleLayerTypeChange = event => {
    const newLayerType = event.target.value.trim();
    const { layer } = this.props;

    if (newLayerType !== LAYER_TYPES.JOIN || layerOps.getType(layer) === LAYER_TYPES.JOIN) {
      this.setState({ geojsonLoaded: true });
    } else {
      this.setState({ geojsonLoaded: false });
    }
    const { layerSubType } = this.state;
    const subTypesForType = getSubTypesForType(newLayerType);
    const newLayerSubType = includes(subTypesForType, layerSubType) ? layerSubType : '';
    this.setState({ layerType: newLayerType, layerSubType: newLayerSubType });
  };

  handleSubmit = async form => {
    const { layer } = this.props;
    const {
      annotationLayerProperties,
      bBoxUpdate,
      bottomLeft,
      excelColumns,
      failedFetchDueToCORS,
      fiwareVersion,
      geojsonProps,
      georefType,
      isFeature,
      isModal,
      layerGeometry,
      layerSubType,
      layerType,
      mapLocation,
      thematicKeyList,
      topLeft,
      topRight
    } = this.state;

    const existingLayer = layerOps.getId(layer);
    const methodToCall = !isEmpty(existingLayer) ? 'layers.update' : 'layers.insert';

    const shouldNotSaveLayer = layerNotSupported(layerType, layerSubType);

    if (shouldNotSaveLayer) {
      Bert.alert('Layer Not Supported Yet', 'warning');
      return '';
    }

    const newLayer = {
      name: form.name.value.trim(),
      description: form.description.value.trim(),
      isFeature,
      layerType,
      layerSubType
    };

    if (!layerOps.isThematic(newLayer))
      assign(newLayer, {
        imageUrl: form.imageUrl.value.trim(),
        maxZoom: Number(form.maxZoom.value.trim()),
        minZoom: Number(form.minZoom.value.trim())
      });

    if (layerOps.isSpatial(newLayer))
      assign(newLayer, {
        attribution: form.attribution.value.trim()
      });
    // if (isFeature) {
    //   assign(newLayer, {
    //     isFeature
    //   });
    // }
    if (bBoxUpdate && !isEmpty(mapLocation)) {
      assign(newLayer, {
        mapLocation
      });
    }

    if (layerOps.isTileServer(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        subdomains: form.subdomains.value.trim()
      });

    if (layerOps.isWMS(newLayer) || layerOps.isTMS(newLayer) || layerOps.is3DTile(newLayer))
      newLayer.url = form.url.value.trim();

    if (layerOps.isGeoTiff(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geoTiffResolution: Number(form.geoTiffResolution.value.trim())
      });

    if (layerOps.isWMTS(newLayer)) {
      newLayer.url = form.url.value.trim();
      if (failedFetchDueToCORS)
        assign(newLayer, {
          wmtsLayer: form.layerNamesInputWMTS.value.trim(),
          tileMatrixSet: form.tileMatrixSetInput.value.trim(),
          wmtsStyle: form.styleInput.value.trim(),
          imageFormat: form.formatInput.value.trim()
        });
      else
        assign(newLayer, {
          wmtsLayer: form.wmtsLayer.value.trim(),
          tileMatrixSet: form.tileMatrixSet.value.trim(),
          wmtsStyle: form.wmtsStyle.value.trim(),
          imageFormat: form.imageFormat.value.trim()
        });
    }

    if (layerOps.isCSV(newLayer)) {
      const separator = form.separator.value;
      const spatialTableFields = {
        longitude: get(form, ['csvLongitude', 'value'], ''),
        latitude: get(form, ['csvLatitude', 'value'], '')
      };
      const h3Index = get(form, ['h3Index', 'value'], '');
      let newThematicKeyList = thematicKeyList;
      assign(newLayer, {
        url: form.url.value.trim(),
        separator
      });
      switch (true) {
        case layerOps.isThematic(newLayer):
          if (newThematicKeyList.length === 1 && includes(newThematicKeyList[0], separator))
            newThematicKeyList = newThematicKeyList[0].split(separator);
          assign(newLayer, { thematicKeyList: newThematicKeyList });
          break;
        case layerOps.isSpatial(newLayer):
          assign(newLayer, {
            georefType,
            geojsonProps,
            geojsonGeometry: layerGeometry
          });
          if (layerOps.isLongLat(newLayer)) assign(newLayer, { spatialTableFields });
          if (layerOps.isH3(newLayer)) assign(newLayer, { h3Index });
          break;
        default:
          break;
      }
    }

    if (layerOps.isWMS(newLayer)) {
      let selectedLayersValuesAsStrings = '';
      let selectedLayersNamesAsStrings = '';

      const urlQueryParams = parse(form.url.value.substring(form.url.value.indexOf('?') + 1));
      const layersParam = find(urlQueryParams, (param, key) => LAYERS_URL_PARAM_REGEX.test(key));

      if (!isEmpty(layersParam)) {
        selectedLayersValuesAsStrings = layersParam.trim();
        selectedLayersNamesAsStrings = layersParam.trim();
      } else if (failedFetchDueToCORS) {
        selectedLayersValuesAsStrings = form.layerNamesInput.value.trim();
        selectedLayersNamesAsStrings = form.layerNamesInput.value.trim();
      } else {
        const firstIndex = form.layerNamesSelect.selectedIndex;
        const selectedLayersValues = [];
        const selectedLayersNames = [];
        if (firstIndex === -1) return [];
        for (let i = firstIndex; i < form.layerNamesSelect.length; i += 1) {
          const currentOption = form.layerNamesSelect[i];
          if (currentOption.selected) {
            selectedLayersValues.push(currentOption.value);
            selectedLayersNames.push(currentOption.innerText);
          }
        }
        selectedLayersValuesAsStrings = selectedLayersValues.join(',');
        selectedLayersNamesAsStrings = selectedLayersNames.join(',');
      }

      let selectedStyleValueAsString = '';
      const stylesParam = find(urlQueryParams, (param, key) => STYLES_URL_PARAM_REGEX.test(key));
      if (!isEmpty(stylesParam)) {
        selectedStyleValueAsString = stylesParam.trim();
      } else if (failedFetchDueToCORS) {
        const selectedLayersValues = selectedLayersValuesAsStrings.split(',');
        if (selectedLayersValues.length === 1)
          selectedStyleValueAsString = form.styleNamesInput.value.trim();
      } else {
        const selectedLayersValues = selectedLayersValuesAsStrings.split(',');
        if (selectedLayersValues.length === 1)
          selectedStyleValueAsString = form.styleNameSelected.value;
      }

      assign(newLayer, {
        layerNames: selectedLayersValuesAsStrings,
        layerTitles: selectedLayersNamesAsStrings,
        styleName: selectedStyleValueAsString,
        wmsVersion: form.wmsVersion.value.trim()
      });
    }

    if (layerOps.isAnnotationsLayer(newLayer))
      assign(newLayer, {
        geojsonGeometry: annotationLayerProperties.geometry.key,
        annotationLayerProperties
      });
    if (layerOps.isGeoJSON(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geojsonProps,
        geojsonGeometry: layerGeometry
      });

    if (layerOps.isTopoJSON(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geojsonProps,
        geojsonGeometry: layerGeometry
      });

    if (layerOps.isGeoJSONVT(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geojsonProps,
        geojsonGeometry: layerGeometry,
        subdomains: form.subdomains.value.trim()
      });

    if (layerOps.isJoin(newLayer))
      assign(newLayer, {
        geojsonPropToJoin: form.geojsonProp.value,
        thematicKeyToJoin: form.thematicKey.value,
        geojsonId: form.geojsonLayer.value,
        thematicId: form.thematicLayer.value,
        sorensenDiceMinimun: Number(form.sorensenDiceMinimun.value),
        geojsonProps,
        geojsonGeometry: layerGeometry
      });

    if (layerOps.isMapBox(newLayer)) {
      if (form.mapboxLayerNameText)
        assign(newLayer, {
          url: form.url.value.trim(),
          mapboxLayerName: form.mapboxLayerNameText.value.trim(),
          mapboxProps: [],
          geojsonGeometry: layerGeometry,
          subdomains: form.subdomains.value.trim()
        });
      else
        assign(newLayer, {
          url: form.url.value.trim(),
          mapboxLayerName: form.mapboxLayerNameSelect.value.trim(),
          mapboxProps: geojsonProps,
          geojsonGeometry: layerGeometry,
          subdomains: form.subdomains.value.trim()
        });
    }

    if (layerOps.isRealTime(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geojsonProps,
        geojsonGeometry: layerGeometry,
        interval: toNumber(form.interval.value)
      });

    if (layerOps.isAnnotationsRealTime(newLayer))
      assign(newLayer, {
        geojsonGeometry: annotationLayerProperties.geometry.key,
        annotationLayerProperties,
        interval: toNumber(form.interval.value)
      });

    if (layerOps.isOGR(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geojsonProps,
        geojsonGeometry: layerGeometry,
        ogrLayerName: form.ogrLayerName.value,
        ogrAttributes: map(form.ogrAttribute.selectedOptions, 'value').join(',')
      });

    if (layerOps.isPostgreSQL(newLayer)) {
      const connectionParams = {
        schema: form.schema.value.trim(),
        table: form.table.value.trim(),
        host: form.ipaddress.value.trim(),
        user: form.user.value.trim(),
        database: form.database.value.trim(),
        password: form.password.value.trim(),
        port: toNumber(form.port.value)
      };
      if (layerOps.isSpatial(newLayer))
        assign(newLayer, {
          geojsonProps,
          geojsonGeometry: layerGeometry,
          connectionParams
        });
      else
        assign(newLayer, {
          thematicKeyList,
          connectionParams
        });
    }

    if (layerOps.isFiware(newLayer)) {
      const fiwareParams = {
        user: form.user.value.trim(),
        password: form.password.value.trim(),
        host: form.host.value.trim(),
        port: toNumber(form.port.value),
        fiwareURL: form.fiwareURL.value.trim(),
        fiwareApplicationID: get(form, ['fiwareApplicationID', 'value'], ''),
        fiwareApplicationSecret: get(form, ['fiwareApplicationSecret', 'value'], ''),
        fiwareVersion,
        service: form.service.value.trim(),
        subservice: form.subservice.value.trim()
      };
      const spatialTableFields = {
        longitude: get(form, ['csvLongitude', 'value'], ''),
        latitude: get(form, ['csvLatitude', 'value'], '')
      };
      const locationIndex = get(form, ['locationIndex', 'value'], '');
      const addressIndex = get(form, ['addressIndex', 'value'], '');
      if (layerOps.isSpatial(newLayer)) {
        assign(newLayer, {
          url: form.url.value.trim(),
          fiwareParams,
          geojsonProps,
          geojsonGeometry: layerGeometry,
          georefType,
          fiwareLayerName: form.fiwareLayerName.value.trim()
        });
        if (layerOps.isLongLat(newLayer)) assign(newLayer, { spatialTableFields });
        else if (layerOps.isLocation(newLayer)) assign(newLayer, { locationIndex });
        else if (layerOps.isAddress(newLayer)) assign(newLayer, { addressIndex });
      } else {
        assign(newLayer, {
          url: form.url.value.trim(),
          thematicKeyList,
          fiwareParams,
          fiwareLayerName: form.fiwareLayerName.value.trim()
        });
      }
    }

    if (layerOps.isExcel(newLayer) || layerOps.isGoogleSheets(newLayer)) {
      const excelLimits = {
        firstRow: Number(form.excelFirstRow.value),
        lastRow: Number(form.excelLastRow.value)
      };
      if (layerOps.isExcel(newLayer))
        assign(excelLimits, {
          firstColumn: Number(form.excelFirstColumn.value),
          lastColumn: Number(form.excelLastColumn.value)
        });
      if (layerOps.isGoogleSheets(newLayer))
        assign(excelLimits, {
          columns: map(form.excelColumns.selectedOptions, ({ value }) => value)
        });

      assign(newLayer, {
        excelLimits,
        excelSheet: form.excelSheet.value,
        url: form.url.value.trim()
      });

      switch (true) {
        case layerOps.isThematic(newLayer):
          assign(newLayer, { thematicKeyList });
          break;
        case layerOps.isSpatial(newLayer):
          assign(newLayer, {
            geojsonGeometry: layerGeometry,
            geojsonProps,
            georefType,
            thematicKeyList: excelColumns
          });
          if (layerOps.isLongLat(newLayer))
            assign(newLayer, {
              spatialTableFields: {
                latitude: get(form, ['csvLatitude', 'value'], ''),
                longitude: get(form, ['csvLongitude', 'value'], '')
              }
            });
          if (layerOps.isH3(newLayer))
            assign(newLayer, { h3Index: get(form, ['h3Index', 'value'], '') });
          break;
        default:
          break;
      }
    }

    if (layerOps.isIndoor(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geojsonProps,
        geojsonGeometry: layerGeometry,
        indoorLevel: form.indoorLevel.value.trim()
      });

    if (layerOps.isMapBoxGL(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        accessToken: isEmpty(form.accessToken.value.trim())
          ? NO_TOKEN
          : form.accessToken.value.trim()
      });

    if (layerOps.isGeoImage(newLayer))
      assign(newLayer, {
        url: form.url.value.trim(),
        geoImageCorners: { topLeft, topRight, bottomLeft }
      });

    if (!isEmpty(existingLayer)) newLayer._id = existingLayer;
    try {
      const layerId = await methodPromise(methodToCall, newLayer);
      if (isModal) {
        this.props.setIpLayer(layerId);
      } else {
        this.redirectToLayerView(form, existingLayer, layerId);
      }
    } catch (error) {
      handleErrorInClient(error);
    }
    return true;
  };

  handleURLChange = () => {
    const { geojsonLoaded, layerSubType } = this.state;
    if (
      includes(
        [
          LAYER_SUBTYPES.GEOJSON,
          LAYER_SUBTYPES.TOPOJSON,
          LAYER_SUBTYPES.CSV,
          LAYER_SUBTYPES.FIWARE,
          LAYER_SUBTYPES.GEOJSONVT,
          LAYER_SUBTYPES.MAPBOX,
          LAYER_SUBTYPES.REALTIME,
          LAYER_SUBTYPES.OGR,
          LAYER_SUBTYPES.EXCEL,
          LAYER_SUBTYPES.INDOOR
        ],
        layerSubType
      ) &&
      geojsonLoaded
    )
      this.setState({
        geojsonLoading: false,
        geojsonProps: [],
        geojsonLoaded: false,
        mapboxLayerName: '',
        mapboxLayerNames: [],
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON,
        ogrLayers: {}
      });
  };

  joinLayers = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    try {
      const { layerSubType } = this.state;
      const joinedGeoJSON = await joinGeoJSONThematicLayer({
        geojsonId: event.target.form.geojsonLayer.value,
        thematicId: event.target.form.thematicLayer.value,
        geojsonPropToJoin: event.target.form.geojsonProp.value,
        thematicKeyToJoin: event.target.form.thematicKey.value,
        sorensenDiceMinimun: Number(event.target.form.sorensenDiceMinimun.value),
        layerSubType
      });
      const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(joinedGeoJSON);

      this.setState({
        geojsonProps: geojsonProperties,
        layerGeometry: geojsonGeometries[0],
        geojsonLoading: false,
        geojsonLoaded: true
      });
      Bert.alert('File Loaded', 'success');
    } catch (err) {
      this.setState({
        geojsonProps: [],
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON,
        geojsonLoading: false,
        geojsonLoaded: false
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadAnnotationLayerProperties = newProperties => {
    this.setState({
      annotationLayerProperties: newProperties
    });
  };

  load3DTiles = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const jsonUrl = adaptURL(event.target.form.url.value.trim());
    try {
      await loadGeoJSONFromURL(jsonUrl);
      this.setState({
        geojsonLoading: false,
        geojsonLoaded: true
      });
      Bert.alert('File Loaded', 'success');
    } catch (err) {
      this.setState({
        geojsonLoading: false,
        geojsonLoaded: false
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadCSV = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    try {
      const { layerType } = this.state;
      const url = adaptURL(event.target.form.url.value.trim());
      const separator = event.target.form.separator.value;

      const { tableTitle } = await loadCSVTable({ url, separator });

      this.setState({
        geojsonLoaded: LAYER_TYPES.THEMATIC === layerType,
        geojsonLoading: false,
        thematicKeyList: tableTitle
      });
      Bert.alert('File Loaded', 'success');
    } catch (err) {
      this.setState({ geojsonLoaded: false, geojsonLoading: false, thematicKeyList: [] });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadExcelSheetsFromURL = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    try {
      const { layerSubType } = this.state;
      const url = adaptURL(event.target.form.url.value.trim());
      const sheetList = await loadExcelSheets({ url, layerSubType });
      this.setState({ geojsonLoading: false, sheetList });
      Bert.alert('File Loaded', 'success');
    } catch (err) {
      this.setState({ geojsonLoading: false, sheetList: [] });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadExcelColumnsAndRows = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });

    const { layerSubType } = this.state;
    const url = adaptURL(event.target.form.url.value.trim());
    const excelSheet = event.target.value;
    try {
      const { excelColumns, excelTableLength, excelTableWidth } = await loadExcelColumnsAndRows({
        excelSheet,
        layerSubType,
        url
      });
      this.setState({
        excelColumns,
        excelSheet,
        excelTableLength,
        excelTableWidth,
        geojsonLoading: false,
        thematicKeyList: excelColumns
      });
    } catch (err) {
      this.setState({
        excelColumns: [],
        excelSheet: '',
        excelTableLength: 1,
        excelTableWidth: 1,
        geojsonLoading: false,
        thematicKeyList: []
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadExcelTableTitles = async event => {
    const { layerSubType, layerType } = this.state;
    if (LAYER_TYPES.THEMATIC === layerType) {
      this.setState({ geojsonLoading: true, geojsonLoaded: false });
      try {
        const excelSheet = event.target.form.excelSheet.value;
        const url = event.target.form.url.value.trim();
        let specificExcelLimits = {};
        if (layerSubType === LAYER_SUBTYPES.EXCEL)
          specificExcelLimits = {
            firstColumn: Number(event.target.form.excelFirstColumn.value),
            lastColumn: Number(event.target.form.excelLastColumn.value)
          };
        if (layerSubType === LAYER_SUBTYPES.GOOGLE_SHEETS)
          specificExcelLimits = {
            columns: map(event.target.form.excelColumns.selectedOptions, ({ value }) => value)
          };
        const tempLayer = {
          excelLimits: {
            firstRow: Number(event.target.form.excelFirstRow.value),
            lastRow: Number(event.target.form.excelLastRow.value),
            ...specificExcelLimits
          },
          excelSheet,
          layerSubType,
          url
        };
        const { tableTitle } = await loadExcelFromURL(tempLayer);
        this.setState({ geojsonLoaded: true, geojsonLoading: false, thematicKeyList: tableTitle });
      } catch (error) {
        this.setState({ geojsonLoaded: false, geojsonLoading: false, thematicKeyList: [] });
        console.error(error);
        handleErrorInClient(error);
      }
    }
  };

  loadFiware = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    try {
      const { layerType, fiwareVersion } = this.state;
      const loadParameters = {
        user: event.target.form.user.value.trim(),
        password: event.target.form.password.value.trim(),
        host: event.target.form.host.value.trim(),
        port: toNumber(event.target.form.port.value),
        fiwareURL: event.target.form.fiwareURL.value.trim(),
        fiwareTokenURL: event.target.form.url.value.trim(),
        fiwareApplicationID: get(event, ['target', 'form', 'fiwareApplicationID', 'value'], ''),
        fiwareApplicationSecret: get(
          event,
          ['target', 'form', 'fiwareApplicationSecret', 'value'],
          ''
        ),
        fiwareVersion,
        service: event.target.form.service.value.trim(),
        subservice: event.target.form.subservice.value.trim()
      };
      const fiwareLayerName = event.target.form.fiwareLayerName.value.trim();

      let geojson;
      let geojsonProperties;
      let geojsonGeometries;
      let table;
      switch (layerType) {
        case LAYER_TYPES.SPATIAL:
          geojson = await methodPromise('layers.loadFiware', {
            loadParameters,
            georefType: event.target.form.georefType.value.trim(),
            fiwareLayerName,
            spatialTableFields: {
              longitude: get(event, ['target', 'form', 'csvLongitude', 'value'], ''),
              latitude: get(event, ['target', 'form', 'csvLatitude', 'value'], '')
            },
            locationIndex: get(event, ['target', 'form', 'locationIndex', 'value'], ''),
            addressIndex: get(event, ['target', 'form', 'addressIndex', 'value'], '')
          });
          ({ geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson));
          if (geojsonGeometries.length === 1) {
            this.setState({
              geojsonProps: geojsonProperties,
              geojsonLoading: false,
              geojsonLoaded: true,
              layerGeometry: geojsonGeometries[0]
            });
            Bert.alert('File Loaded', 'success');
          } else {
            Bert.alert('The GeoJSON has no geometries/too many geometries', 'danger');
            this.setState({
              geojsonProps: [],
              geojsonLoading: false,
              geojsonLoaded: false,
              layerGeometry: GEOJSON_GEOMETRIES.POLYGON
            });
          }
          break;
        case LAYER_TYPES.THEMATIC:
          table = await methodPromise('layers.loadFiware', {
            loadParameters,
            fiwareLayerName,
            isThematic: true
          });
          Bert.alert('File Loaded', 'success');
          this.setState({
            thematicKeyList: table[0],
            geojsonLoading: false,
            geojsonLoaded: true
          });
          break;
        default:
          break;
      }
    } catch (err) {
      this.setState({
        geojsonLoading: false,
        geojsonProps: [],
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadFiwareAttributes = async event => {
    this.setState({ geojsonLoaded: false });
    try {
      const { fiwareVersion } = this.state;
      const loadParameters = {
        user: event.target.form.user.value.trim(),
        password: event.target.form.password.value.trim(),
        host: event.target.form.host.value.trim(),
        port: toNumber(event.target.form.port.value),
        fiwareURL: event.target.form.fiwareURL.value.trim(),
        fiwareTokenURL: event.target.form.url.value.trim(),
        fiwareApplicationID: get(event, ['target', 'form', 'fiwareApplicationID', 'value'], ''),
        fiwareApplicationSecret: get(
          event,
          ['target', 'form', 'fiwareApplicationSecret', 'value'],
          ''
        ),
        fiwareVersion,
        service: event.target.form.service.value.trim(),
        subservice: event.target.form.subservice.value.trim()
      };
      const fiwareLayerName = event.target.value.trim();
      const thematicKeyList = await methodPromise('layers.loadFiwareAttributes', {
        fiwareLayerName,
        loadParameters
      });
      this.setState({ fiwareLayerName, georefType: '', thematicKeyList });
    } catch (err) {
      handleErrorInClient(err);
    }
  };

  loadFiwareLayers = async event => {
    this.setState({ geojsonLoaded: false });
    try {
      const { fiwareVersion } = this.state;
      const loadParameters = {
        user: event.target.form.user.value.trim(),
        password: event.target.form.password.value.trim(),
        host: event.target.form.host.value.trim(),
        fiwareURL: event.target.form.fiwareURL.value.trim(),
        fiwareTokenURL: event.target.form.url.value.trim(),
        fiwareApplicationID: get(event, ['target', 'form', 'fiwareApplicationID', 'value'], ''),
        fiwareApplicationSecret: get(
          event,
          ['target', 'form', 'fiwareApplicationSecret', 'value'],
          ''
        ),
        fiwareVersion,
        service: event.target.form.service.value.trim(),
        subservice: event.target.form.subservice.value.trim(),
        port: toNumber(event.target.form.port.value)
      };
      const fiwareLayers = await methodPromise('layers.loadFiwareLayers', loadParameters);
      this.setState({ fiwareLayerName: '', fiwareLayers, georefType: '', thematicKeyList: [] });
    } catch (err) {
      handleErrorInClient(err);
    }
  };

  loadFiwareVersion = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const fiwareTokenURL = event.target.form.url.value.trim();
    if (!isEmpty(fiwareTokenURL)) {
      const connectionParameters = { fiwareTokenURL };
      try {
        const fiwareVersion = await methodPromise('layers.getTokenVersion', connectionParameters);
        this.setState({ geojsonLoading: false, fiwareVersion });
        Bert.alert('File Loaded', 'success');
      } catch (err) {
        this.setState({ geojsonLoading: false });
        handleErrorInClient(err);
      }
    } else {
      this.setState({ geojsonLoading: false });
      Bert.alert('No URL found', 'danger');
    }
  };

  loadGeoJSON = async event => {
    const { layerSubType } = this.state;
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const geojsonUrl = adaptURL(event.target.form.url.value.trim());
    try {
      let geojson;
      if (layerSubType !== LAYER_SUBTYPES.INDOOR) geojson = await loadGeoJSONFromURL(geojsonUrl);
      else geojson = await loadGeoJSONIndoorFromURL(geojsonUrl);
      const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson);
      if (geojsonGeometries.length === 1) {
        this.setState({
          geojsonProps: geojsonProperties,
          geojsonLoading: false,
          geojsonLoaded: true,
          layerGeometry: geojsonGeometries[0]
        });
        Bert.alert('File Loaded', 'success');
      } else {
        Bert.alert('The GeoJSON has no geometries/too many geometries', 'danger');
        this.setState({
          geojsonProps: [],
          geojsonLoading: false,
          geojsonLoaded: false,
          layerGeometry: GEOJSON_GEOMETRIES.POLYGON
        });
      }
    } catch (err) {
      this.setState({
        geojsonLoading: false,
        geojsonProps: [],
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadMapBoxFromLayerName = async event => {
    if (!isEmpty(event.target.value.trim())) {
      this.setState({ geojsonLoading: true, geojsonLoaded: false });
      try {
        const mapboxUrl = adaptURL(event.target.form.url.value.trim());
        const mapboxLayerName = event.target.value.trim();
        const { adaptedUrl } = getGeoJSONURLFromMapbox(mapboxUrl);
        const layerTileParams = {};
        if (event.target.form.mapboxLayerX) {
          layerTileParams.x = event.target.form.mapboxLayerX.value.trim();
          layerTileParams.z = event.target.form.mapboxLayerZ.value.trim();
          if (adaptedUrl.match(NEGATIVE_Y_PARAM_REGEX))
            layerTileParams['-y'] = event.target.form.mapboxLayerY.value.trim();
          else layerTileParams.y = event.target.form.mapboxLayerY.value.trim();
        }
        const mapboxGeoJSON = await getGeoJSONFromMapBox({
          layerTileParams,
          mapboxLayerName,
          url: adaptedUrl
        });
        const { geojsonGeometries, geojsonProperties } = parseGeoJSONProperties(mapboxGeoJSON);
        this.setState({
          geojsonLoaded: true,
          geojsonLoading: false,
          geojsonProps: geojsonProperties,
          layerGeometry: geojsonGeometries[0],
          mapboxLayerName
        });
        Bert.alert('File Loaded', 'success');
      } catch (err) {
        this.setState({
          geojsonProps: [],
          mapboxLayerName: '',
          geojsonLoading: false,
          geojsonLoaded: false,
          layerGeometry: GEOJSON_GEOMETRIES.POLYGON
        });
        console.error(err);
        handleErrorInClient(err);
      }
    } else {
      this.setState({
        geojsonProps: [],
        mapboxLayerName: '',
        geojsonLoading: false,
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      Bert.alert('Please select a Layer Name', 'warning');
    }
  };

  loadMapBoxLayersOrGeoJSON = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    try {
      const mapboxUrl = adaptURL(event.target.form.url.value.trim());
      const { adaptedUrl, layerName } = getGeoJSONURLFromMapbox(mapboxUrl);
      let geojson;
      if (!isEmpty(layerName)) {
        geojson = await loadGeoJSONFromURL(adaptedUrl);
        const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson);
        this.setState({
          geojsonProps: geojsonProperties,
          mapboxLayerName: layerName,
          layerGeometry: geojsonGeometries[0],
          isExternal: false,
          geojsonLoading: false,
          geojsonLoaded: true
        });
        Bert.alert('File Loaded', 'success');
      } else {
        const { layer } = this.props;
        const defaultMapCenter = isEmpty(layerOps.getMapLocation(layer))
          ? turf.point([0, 0], { zoom: 0 })
          : layerOps.getMapLocation(layer);
        const layerTileParams = coordinatesToXYZ(defaultMapCenter);
        if (!isNil(event.target.form.mapboxLayerX)) {
          layerTileParams.x = event.target.form.mapboxLayerX.value;
          layerTileParams.z = event.target.form.mapboxLayerZ.value;
          if (adaptedUrl.match(NEGATIVE_Y_PARAM_REGEX))
            layerTileParams['-y'] = event.target.form.mapboxLayerY.value;
          else layerTileParams.y = event.target.form.mapboxLayerY.value;
        }
        const mapboxLayerNames = await loadMapBoxLayerNames(adaptedUrl, layerTileParams);
        this.setState({
          geojsonLoading: false,
          isExternal: true,
          mapboxLayerName: '',
          mapboxLayerNames
        });
      }
    } catch (err) {
      this.setState({
        geojsonProps: [],
        mapboxLayerName: '',
        mapboxLayerNames: [],
        geojsonLoading: false,
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadOGRGeoJSON = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const url = adaptURL(event.target.form.url.value.trim());
    const layerName = event.target.form.ogrLayerName.value;
    const attribute = map(event.target.form.ogrAttribute.selectedOptions, 'value');
    try {
      const geojson = await methodPromise('layers.loadOGRGeoJSON', {
        url,
        layerName,
        attribute
      });
      const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson);
      if (geojsonGeometries.length === 1) {
        this.setState({
          geojsonProps: geojsonProperties,
          geojsonLoading: false,
          geojsonLoaded: true,
          layerGeometry: geojsonGeometries[0]
        });
        Bert.alert('File Loaded', 'success');
      } else {
        Bert.alert('The GeoJSON has no geometries/too many geometries', 'danger');
        this.setState({
          geojsonProps: [],
          geojsonLoading: false,
          geojsonLoaded: false,
          layerGeometry: GEOJSON_GEOMETRIES.POLYGON
        });
      }
    } catch (err) {
      this.setState({
        geojsonLoading: false,
        geojsonProps: [],
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadOGRNameAndAttributes = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const url = adaptURL(event.target.form.url.value.trim());
    try {
      const ogrLayers = await methodPromise('layers.loadOGRGeoJSON', {
        url,
        layerName: null,
        attribute: null
      });
      if (hasIn(ogrLayers, 'layers')) {
        this.setState({
          ogrLayers,
          geojsonLoading: false,
          geojsonLoaded: false
        });
        Bert.alert('File Loaded', 'success');
      } else {
        throw new Error(get(ogrLayers, 'message') || get(ogrLayers, 'result'));
      }
    } catch (err) {
      this.setState({
        ogrLayers: {},
        geojsonLoading: false,
        geojsonLoaded: false
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadPostgreSQL = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    try {
      const { layerType } = this.state;
      const loadParameters = {
        schema: event.target.form.schema.value.trim(),
        table: event.target.form.table.value.trim(),
        host: event.target.form.ipaddress.value.trim(),
        user: event.target.form.user.value.trim(),
        database: event.target.form.database.value.trim(),
        password: event.target.form.password.value.trim(),
        port: toNumber(event.target.form.port.value)
      };
      let geojson;
      let geojsonProperties;
      let geojsonGeometries;
      let table;
      const attributes = map(event.target.form.postgreSQLAttributes.selectedOptions, 'value');
      switch (layerType) {
        case LAYER_TYPES.SPATIAL:
          geojson = await methodPromise('layers.loadPostgreSQL', { loadParameters, attributes });
          ({ geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson));
          if (geojsonGeometries.length === 1) {
            this.setState({
              geojsonProps: geojsonProperties,
              geojsonLoading: false,
              geojsonLoaded: true,
              layerGeometry: geojsonGeometries[0]
            });
            Bert.alert('File Loaded', 'success');
          } else {
            Bert.alert('The GeoJSON has no geometries/too many geometries', 'danger');
            this.setState({
              geojsonProps: [],
              geojsonLoading: false,
              geojsonLoaded: false,
              layerGeometry: GEOJSON_GEOMETRIES.POLYGON
            });
          }
          break;
        case LAYER_TYPES.THEMATIC:
          table = await methodPromise('layers.loadPostgreSQL', {
            loadParameters,
            attributes,
            isThematic: true
          });
          Bert.alert('File Loaded', 'success');
          this.setState({
            thematicKeyList: table[0],
            geojsonLoading: false,
            geojsonLoaded: true
          });
          break;
        default:
          break;
      }
    } catch (err) {
      this.setState({
        geojsonLoading: false,
        geojsonProps: [],
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadSpatialGeoJSON = async event => {
    const { georefType, layerSubType } = this.state;
    const url = adaptURL(event.target.form.url.value.trim());
    const h3Index = get(event, ['target', 'form', 'h3Index', 'value'], '');
    const spatialTableFields = {
      longitude: get(event, ['target', 'form', 'csvLongitude', 'value'], ''),
      latitude: get(event, ['target', 'form', 'csvLatitude', 'value'], '')
    };
    const separator = get(event, ['target', 'form', 'separator', 'value'], '');
    const excelSheet = get(event, ['target', 'form', 'excelSheet', 'value'], '');
    let excelLimits;
    if (layerSubType === LAYER_SUBTYPES.EXCEL) {
      excelLimits = {
        firstRow: Number(get(event, ['target', 'form', 'excelFirstRow', 'value'], '')),
        lastRow: Number(get(event, ['target', 'form', 'excelLastRow', 'value'], '')),
        firstColumn: Number(get(event, ['target', 'form', 'excelFirstColumn', 'value'], '')),
        lastColumn: Number(get(event, ['target', 'form', 'excelLastColumn', 'value'], ''))
      };
    }
    if (layerSubType === LAYER_SUBTYPES.GOOGLE_SHEETS) {
      excelLimits = {
        firstRow: Number(get(event, ['target', 'form', 'excelFirstRow', 'value'], '')),
        lastRow: Number(get(event, ['target', 'form', 'excelLastRow', 'value'], '')),
        columns: map(
          get(event, ['target', 'form', 'excelColumns', 'selectedOptions'], ''),
          ({ value }) => value
        )
      };
    }
    if (
      (!isEmpty(spatialTableFields.longitude) && !isEmpty(spatialTableFields.latitude)) ||
      !isEmpty(h3Index)
    ) {
      this.setState({ geojsonLoading: true, geojsonLoaded: false });
      try {
        let geojson;
        switch (layerSubType) {
          case LAYER_SUBTYPES.CSV:
            geojson = await getGeoJSONFromCSV({
              georefType,
              h3Index,
              separator,
              spatialTableFields,
              url
            });
            break;
          case LAYER_SUBTYPES.EXCEL:
          case LAYER_SUBTYPES.GOOGLE_SHEETS:
            geojson = await getGeoJSONFromExcel({
              excelSheet,
              excelLimits,
              georefType,
              h3Index,
              layerSubType,
              spatialTableFields,
              url
            });
            break;
          default:
            geojson = {};
            break;
        }
        const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson);

        if (geojsonGeometries.length === 1) {
          this.setState({
            geojsonProps: geojsonProperties,
            geojsonLoading: false,
            geojsonLoaded: true,
            layerGeometry: geojsonGeometries[0]
          });
          Bert.alert('File Loaded', 'success');
        } else {
          if (layerSubType !== LAYER_SUBTYPES.FIWARE)
            Bert.alert('The GeoJSON has no geometries/too many geometries', 'danger');
          this.setState({
            geojsonLoaded: false,
            geojsonLoading: false,
            geojsonProps: [],
            layerGeometry: GEOJSON_GEOMETRIES.POLYGON
          });
        }
      } catch (err) {
        this.setState({
          geojsonLoaded: false,
          geojsonLoading: false,
          geojsonProps: [],
          layerGeometry: GEOJSON_GEOMETRIES.POLYGON
        });
        console.error(err);
        handleErrorInClient(err);
      }
    }
  };

  loadTopoJSON = async event => {
    this.setState({ geojsonLoading: true, geojsonLoaded: false });
    const topojsonUrl = adaptURL(event.target.form.url.value.trim());
    try {
      const topojsonFile = await loadGeoJSONFromURL(topojsonUrl);
      const objectKey = keys(topojsonFile.objects)[0];
      const geojson = topojson.feature(topojsonFile, topojsonFile.objects[objectKey]);
      const { geojsonProperties, geojsonGeometries } = parseGeoJSONProperties(geojson);
      if (geojsonGeometries.length === 1) {
        this.setState({
          geojsonProps: geojsonProperties,
          geojsonLoading: false,
          geojsonLoaded: true,
          layerGeometry: geojsonGeometries[0]
        });
        Bert.alert('File Loaded', 'success');
      } else {
        Bert.alert('The GeoJSON has no geometries/too many geometries', 'danger');
        this.setState({
          geojsonProps: [],
          geojsonLoading: false,
          geojsonLoaded: false,
          layerGeometry: GEOJSON_GEOMETRIES.POLYGON
        });
      }
    } catch (err) {
      this.setState({
        geojsonLoading: false,
        geojsonProps: [],
        geojsonLoaded: false,
        layerGeometry: GEOJSON_GEOMETRIES.POLYGON
      });
      console.error(err);
      handleErrorInClient(err);
    }
  };

  loadWMSLayers = async event => {
    this.setState({ urlIncludesLayers: false, failedFetchDueToCORS: false });
    const wmsUrl = event.target.form.url.value.trim();
    try {
      let wmsLayerTitles = '';
      let wmsLayerNames = '';
      const urlQueryParams = parse(wmsUrl.substring(wmsUrl.indexOf('?') + 1));
      const layersParam = find(urlQueryParams, (param, key) => LAYERS_URL_PARAM_REGEX.test(key));
      const connectionParams = {
        user: event.target.form.user.value.trim(),
        password: event.target.form.password.value.trim()
      };
      if (!isNil(layersParam)) {
        Bert.alert('Layers already defined in URL', 'info');
        wmsLayerTitles = layersParam;
        wmsLayerNames = layersParam;
      } else {
        ({ layerTitles: wmsLayerTitles, layerNames: wmsLayerNames } = await getWMSLayerNamesFromURL(
          wmsUrl,
          connectionParams
        ));
        if (isEmpty(wmsLayerTitles) && isEmpty(wmsLayerNames))
          Bert.alert('No layers found', 'warning');
      }
      this.setState({ urlIncludesLayers: !isNil(layersParam), wmsLayerNames, wmsLayerTitles });
      if (wmsLayerNames.split().length > 1) this.setState({ preselectedStyles: [] });

      Bert.alert('File Loaded', 'success');
    } catch (err) {
      console.error(err);
      if (err.message === FAILED_FETCH_MESSAGE) this.setState({ failedFetchDueToCORS: true });
      handleErrorInClient(err);
    }
  };

  loadWMSStyles = async event => {
    try {
      const { failedFetchDueToCORS } = this.state;
      const wmsUrl = event.target.form.url.value.trim();
      const urlQueryParams = parse(wmsUrl.substring(wmsUrl.indexOf('?') + 1));
      const stylesParam = find(urlQueryParams, (param, key) => STYLES_URL_PARAM_REGEX.test(key));
      const connectionParams = {
        user: event.target.form.user.value.trim(),
        password: event.target.form.password.value.trim()
      };
      if (event.target.form.layerNamesSelect.selectedOptions.length > 1) {
        this.setState({ preselectedStyles: [] });
        Bert.alert('Select only one layer to show the styles', 'warning');
      } else if (!isNil(stylesParam)) {
        Bert.alert('Layers already defined in URL', 'info');
        const preselectedStyles = [stylesParam];
        this.setState({ preselectedStyles });
      } else if (failedFetchDueToCORS) {
        if (event.target.form.layerNamesInput.value.split().length === 1) {
          this.setState({ preselectedStyles: [] });
        }
      } else if (event.target.form.layerNamesSelect.selectedOptions.length === 1) {
        const preselectedStyles = await getWMSStyleNamesFromURL(
          event.target.form.url.value.trim(),
          event.target.form.layerNamesSelect.selectedOptions[0].value,
          connectionParams
        );
        this.setState({ preselectedStyles });
      } else {
        Bert.alert('Select only one layer to show the styles', 'warning');
      }
    } catch (err) {
      console.error(err);
      if (err.message === FAILED_FETCH_MESSAGE) {
        this.setState({ failedFetchDueToCORS: true });
      }
      handleErrorInClient(err);
    }
  };

  loadWMTSLayer = async event => {
    this.setState({ urlIncludesWMTSOptions: false, failedFetchDueToCORS: false });
    const wmtsUrl = event.target.form.url.value.trim();
    try {
      let wmtsLayerIds = '';
      const urlQueryParams = parse(wmtsUrl.substring(wmtsUrl.indexOf('?') + 1));
      const layerParam = find(urlQueryParams, (param, key) => LAYER_URL_PARAM_REGEX.test(key));
      if (!isNil(layerParam)) {
        Bert.alert('Layers already defined in URL', 'info');
        wmtsLayerIds = layerParam;
      } else {
        ({ layerOptions: wmtsLayerIds } = await getWMTSParamsFromURL(
          wmtsUrl,
          event.target.form.wmtsLayer.value.trim()
        ));

        if (isEmpty(wmtsLayerIds)) {
          Bert.alert('No layers found', 'warning');
        }
      }
      this.setState({
        urlIncludesWMTSOptions: !isNil(layerParam),
        wmtsLayerIds
      });
      Bert.alert('File Loaded', 'success');
    } catch (err) {
      console.error(err);
      if (err.message === FAILED_FETCH_MESSAGE) {
        this.setState({ failedFetchDueToCORS: true });
      }
      handleErrorInClient(err);
    }
  };

  loadWMTSOptions = async event => {
    this.setState({
      urlIncludesWMTSOptions: false,
      failedFetchDueToCORS: false,
      wmtsLayerId: event.target.form.wmtsLayer.value.trim()
    });
    const wmtsUrl = event.target.form.url.value.trim();
    try {
      let wmtsStyleId = '';
      let wmtsTileMatrixSetId = '';
      let wmtsFormatId = '';
      const urlQueryParams = parse(wmtsUrl.substring(wmtsUrl.indexOf('?') + 1));
      const styleParam = find(urlQueryParams, (param, key) => STYLE_URL_PARAM_REGEX.test(key));
      const tileMatrixSetParam = find(urlQueryParams, (param, key) =>
        TILEMATRIXSET_URL_PARAM_REGEX.test(key)
      );
      const formatParam = find(urlQueryParams, (param, key) => FORMAT_URL_PARAM_REGEX.test(key));
      if (!isNil(styleParam) && !isNil(tileMatrixSetParam) && !isNil(formatParam)) {
        Bert.alert('Options already defined in URL', 'info');
        wmtsStyleId = styleParam;
        wmtsTileMatrixSetId = tileMatrixSetParam;
        wmtsFormatId = formatParam;
      } else {
        ({
          styleOptions: wmtsStyleId,
          tileMatrixSetOptions: wmtsTileMatrixSetId,
          formatOptions: wmtsFormatId
        } = await getWMTSParamsFromURL(wmtsUrl, event.target.form.wmtsLayer.value.trim()));

        if (isEmpty(wmtsStyleId) && isEmpty(wmtsTileMatrixSetId) && isEmpty(wmtsFormatId)) {
          Bert.alert('No layers found', 'warning');
        }
      }
      this.setState({
        urlIncludesWMTSOptions:
          !isNil(styleParam) && !isNil(tileMatrixSetParam) && !isNil(formatParam),
        wmtsStyleId,
        wmtsTileMatrixSetId,
        wmtsFormatId
      });
      Bert.alert('File Loaded', 'success');
    } catch (err) {
      console.error(err);
      if (err.message === FAILED_FETCH_MESSAGE) {
        this.setState({ failedFetchDueToCORS: true });
      }
      handleErrorInClient(err);
    }
  };

  updateShowStyleField = newShowStyleField => {
    const { selectedLayersNamesAsStrings } = this.state;
    if (!isEmpty(selectedLayersNamesAsStrings)) Bert.alert('Select only one Layer', 'danger');
    this.setState({ showStylesField: newShowStyleField });
  };

  setIsFeature = () => {
    const { isFeature } = this.state;
    this.setState({ isFeature: !isFeature });
  };

  setUrlDefault = value => {
    this.setState({ defaultUrl: value });
  };

  redirectToLayerView(form, existingLayer, layerId) {
    const { history } = this.props;
    const confirmation = !isEmpty(existingLayer) ? 'Layer updated!' : 'Layer added!';
    form.reset();
    Bert.alert(confirmation, 'success');
    history.push(`/layers/${layerId}`);
  }

  render() {
    const { layer, isFree, unfilteredCount, isBasic, deployConfig } = this.props;
    const { failedFetchDueToCORS, geojsonLoaded, layerSubType, layerType, layerTypes } = this.state;
    const isNewLayer = isEmpty(layerOps.getId(layer));

    const validationProps = {
      rules: {
        name: {
          required: true
        },
        description: {
          required: false
        },
        layerType: {
          required: true
        },
        layerSubType: {
          required: true
        },
        layerNames: {
          required: false
        },
        url: {
          required: includes(
            [
              LAYER_SUBTYPES.CSV,
              LAYER_SUBTYPES.EXCEL,
              LAYER_SUBTYPES.FIWARE,
              LAYER_SUBTYPES.GEOIMAGE,
              LAYER_SUBTYPES.GEOJSON,
              LAYER_SUBTYPES.TOPOJSON,
              LAYER_SUBTYPES.GEOTIFF,
              LAYER_SUBTYPES.GEOJSONVT,
              LAYER_SUBTYPES.INDOOR,
              LAYER_SUBTYPES.MAPBOX,
              LAYER_SUBTYPES.MAPBOXGL,
              LAYER_SUBTYPES.OGR,
              LAYER_SUBTYPES.REALTIME,
              LAYER_SUBTYPES.TILE_SERVER,
              LAYER_SUBTYPES.TMS,
              LAYER_SUBTYPES.WMS,
              LAYER_SUBTYPES.WMTS
            ],
            layerSubType
          )
        },
        imageUrl: {
          required: false
        },
        maxZoom: {
          required: false
        },
        minZoom: {
          requires: false
        },
        attribution: {
          required: layerType === LAYER_TYPES.SPATIAL
        },
        subdomains: {
          required: false
        },
        layerNamesSelect: {
          required: layerSubType === LAYER_SUBTYPES.WMS && !failedFetchDueToCORS
        },
        wmtsLayer: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && !failedFetchDueToCORS
        },
        layerNamesInputWMTS: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && failedFetchDueToCORS
        },
        layerNamesInput: {
          required: layerSubType === LAYER_SUBTYPES.WMS && failedFetchDueToCORS
        },
        layerStylesInput: {
          required: layerSubType === LAYER_SUBTYPES.WMS && failedFetchDueToCORS
        },
        tileMatrixSet: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && !failedFetchDueToCORS
        },
        tileMatrixSetInput: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && failedFetchDueToCORS
        },
        imageFormat: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && !failedFetchDueToCORS
        },
        formatInput: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && failedFetchDueToCORS
        },
        wmtsStyle: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && !failedFetchDueToCORS
        },
        styleInput: {
          required: layerSubType === LAYER_SUBTYPES.WMTS && failedFetchDueToCORS
        },
        geojsonLayer: {
          required: layerType === LAYER_TYPES.JOIN
        },
        geojsonProp: {
          required: layerType === LAYER_TYPES.JOIN
        },
        thematicLayer: {
          required: layerType === LAYER_TYPES.JOIN
        },
        thematicKey: {
          required: layerType === LAYER_TYPES.JOIN
        },
        sorensenDiceMinimun: {
          required: layerType === LAYER_TYPES.JOIN
        },
        mapboxLayerName: {
          required: layerSubType === LAYER_SUBTYPES.MAPBOX
        },
        mapboxPropertyName: {
          required: layerSubType === LAYER_SUBTYPES.MAPBOX
        },
        mapServerElementType: {
          required: layerSubType === LAYER_SUBTYPES.MAPBOX
        },
        interval: {
          required:
            layerSubType === LAYER_SUBTYPES.REALTIME ||
            layerSubType === LAYER_SUBTYPES.ANNOTATIONSREALTIME
        },
        schema: {
          required: layerSubType === LAYER_SUBTYPES.POSTGRESQL
        },
        table: {
          required: layerSubType === LAYER_SUBTYPES.POSTGRESQL
        },
        ipaddress: {
          required: layerSubType === LAYER_SUBTYPES.POSTGRESQL
        },
        user: {
          required: includes([LAYER_SUBTYPES.POSTGRESQL, LAYER_SUBTYPES.FIWARE], layerSubType)
        },
        database: {
          required: layerSubType === LAYER_SUBTYPES.POSTGRESQL
        },
        password: {
          required: includes([LAYER_SUBTYPES.POSTGRESQL, LAYER_SUBTYPES.FIWARE], layerSubType)
        },
        port: {
          required: includes([LAYER_SUBTYPES.POSTGRESQL, LAYER_SUBTYPES.FIWARE], layerSubType)
        },
        host: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        service: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        subservice: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        fiwareApplicationID: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        fiwareApplicationSecret: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        fiwareURL: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        fiwareLayerName: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE
        },
        georefType: {
          required: layerSubType === LAYER_SUBTYPES.FIWARE && layerType === LAYER_TYPES.SPATIAL
        },
        ogrLayerName: {
          required: layerSubType === LAYER_SUBTYPES.OGR
        },
        ogrAttribute: {
          required: layerSubType === LAYER_SUBTYPES.OGR
        },
        separator: {
          required: layerSubType === LAYER_SUBTYPES.CSV
        },
        excelSheet: {
          required: layerSubType === LAYER_SUBTYPES.EXCEL
        },
        excelFirstRow: {
          required: layerSubType === LAYER_SUBTYPES.EXCEL
        },
        excelLastRow: {
          required: layerSubType === LAYER_SUBTYPES.EXCEL
        },
        geoTiffResolution: {
          required: layerType === LAYER_SUBTYPES.GEOTIFF
        },
        accessToken: {
          required: false
        }
      },
      messages: {
        name: {
          required: 'Need a name in here, Seuss.'
        },
        layerType: {
          required: 'This needs a layer type.'
        },
        layerSubType: {
          required: 'This needs a layer subtype.'
        },
        url: {
          required: 'You need to provide the URL'
        },
        attribution: {
          required: 'You need to provide attribution for a tiled server layer'
        },
        layerNamesSelect: {
          required: 'You need to select at least one layer for the WMS'
        },
        layerNamesInput: {
          required: 'You need to provide at least one layer for the WMS'
        },
        layerStylesInput: {
          required: 'You need to provide at least one style for the WMS'
        },
        tileMatrixSet: {
          required: 'You need to select at least one tile matrix set for the WMTS'
        },
        tileMatrixSetInput: {
          required: 'You need to provide at least one tile matrix set for the WMTS'
        },
        imageFormat: {
          required: 'You need to provide at least one format for the WMTS'
        },
        formatInput: {
          required: 'You need to provide at least one format for the WMTS'
        },
        wmtsStyle: {
          required: 'You need to select at least one style for the WMTS'
        },
        styleInput: {
          required: 'You need to provide at least one style for the WMTS'
        },
        wmtsLayer: {
          required: 'You need to select at least one layer for the WMTS'
        },
        layerNamesInputWMTS: {
          required: 'You need to provide at least one layer for the WMTS'
        },
        geojsonLayer: {
          required: 'You need to select a spatial layer to join'
        },
        geojsonProp: {
          required: 'You need to select a property from the spatial layer'
        },
        geoTiffResolution: {
          required: 'You need to select a resolution for the GeoTiff layer'
        },
        thematicLayer: {
          required: 'You need to select a thematic layer to join'
        },
        thematicKey: {
          required: 'You need to select a property from the thematic layer'
        },
        sorensenDiceMinimun: {
          required: 'You need a minimun value for the Sorensen-Dice join'
        },
        mapboxLayerName: {
          required: 'You need a layer name for the MapBox'
        },
        mapboxPropertyName: {
          required: 'You need a property name for the MapBox'
        },
        mapServerElementType: {
          required: 'You need to select the type of the property'
        },
        interval: {
          required: 'You need to select a refreshing time'
        },
        schema: {
          required: 'You need to set a schema'
        },
        table: {
          required: 'You need to set a refreshing time'
        },
        ipaddress: {
          required: 'You need to set an ip address'
        },
        user: {
          required: 'You need to set a user name'
        },
        database: {
          required: 'You need to set a database'
        },
        password: {
          required: 'You need to set a password'
        },
        port: {
          required: 'You need to set a port number'
        },
        ogrLayerName: {
          required: 'You need to set a layer name'
        },
        ogrAttribute: {
          required: 'You need to set at least one attribute'
        },
        separator: {
          required: 'You need to set a separator for the file'
        },
        excelSheet: {
          required: 'You need to set a sheet name for the layer'
        },
        excelFirstRow: {
          required: 'You need to set a first row to correctly read the excel'
        },
        excelLastRow: {
          required: 'You need to set a last row to correctly read the excel'
        }
      },
      submitHandler: form => {
        if (!isFree && !isBasic) {
          this.handleSubmit(form);
        } else if (isFree && unfilteredCount < deployConfig.Roles.Free.LayersLimit) {
          this.handleSubmit(form);
        } else if (isBasic && unfilteredCount < deployConfig.Roles.Basic.LayersLimit) {
          this.handleSubmit(form);
        } else {
          Bert.alert('You need to upgrade your plan to add more Layers', 'info');
        }
      }
    };
    return (
      <Validation {...validationProps}>
        <Form onSubmit={event => event.preventDefault()}>
          <Row>
            <Col xs={12} md={8} lg={6} xl={4}>
              <Form.Group>
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  defaultValue={layerOps.getName(layer)}
                  placeholder="Oh, The Places You'll Go!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  defaultValue={layerOps.getDescription(layer)}
                  placeholder="Congratulations! Today is your day. You're off to Great Places! You're
              off and away!"
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Layer Type</Form.Label>
                <Form.Control
                  as="select"
                  name="layerType"
                  value={layerType}
                  onChange={this.handleLayerTypeChange}
                >
                  {map(layerTypes.properties, ({ value, humanValue }) => {
                    return (
                      <option value={value} key={value}>
                        {humanValue}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
              <LayerSubTypeField
                changeLayerSubType={this.handleLayerSubTypeChange}
                layerType={layerType}
                layerSubType={layerSubType}
                isFree={isFree}
              />
              <LayerFields
                changeLayerEditorStateProperty={this.changeLayerEditorStateProperty}
                handleURLChange={this.handleURLChange}
                setIsFeature={this.setIsFeature}
                setUrlDefault={this.setUrlDefault}
                joinLayers={this.joinLayers}
                loadAnnotationLayerProperties={this.loadAnnotationLayerProperties}
                loadCSV={this.loadCSV}
                loadExcel={this.loadExcelSheetsFromURL}
                loadExcelColumnsAndRows={this.loadExcelColumnsAndRows}
                loadExcelTableTitles={this.loadExcelTableTitles}
                loadFiware={this.loadFiware}
                loadFiwareAttributes={this.loadFiwareAttributes}
                loadFiwareLayers={this.loadFiwareLayers}
                loadFiwareVersion={this.loadFiwareVersion}
                loadGeoJSON={this.loadGeoJSON}
                loadMapBoxFromLayerName={this.loadMapBoxFromLayerName}
                loadMapBoxLayersOrGeoJSON={this.loadMapBoxLayersOrGeoJSON}
                loadOGR={this.loadOGRNameAndAttributes}
                loadOGRGeoJSON={this.loadOGRGeoJSON}
                loadPostgreSQL={this.loadPostgreSQL}
                loadSpatialGeoJSON={this.loadSpatialGeoJSON}
                loadTopoJSON={this.loadTopoJSON}
                load3DTiles={this.load3DTiles}
                loadWMSStyles={this.loadWMSStyles}
                loadWMSLayers={this.loadWMSLayers}
                loadWMTSOptions={this.loadWMTSOptions}
                loadWMTSLayer={this.loadWMTSLayer}
                updateShowStyleField={this.updateShowStyleField}
                {...this.state}
                {...this.props}
              />
            </Col>
          </Row>
          <br />
          <Button type="submit" variant="outline-success" disabled={!geojsonLoaded}>
            {isNewLayer ? 'Add Layer' : 'Save Changes'}
          </Button>
        </Form>
      </Validation>
    );
  }
}

LayerEditor.defaultProps = {
  layer: {
    name: '',
    description: '',
    layerType: LAYER_TYPES.SPATIAL,
    layerSubType: LAYER_SUBTYPES.TMS
  },
  annotationLayerProperties: {
    annotationsId: [],
    geojsonAnnotations: {},
    geometry: { name: 'Anotaciones', key: 'Point' },
    projectId: '',
    videosId: []
  },
  setIpLayer: () => {}
};

LayerEditor.propTypes = {
  annotationLayerProperties: PropTypes.object,
  deployEnv: PropTypes.string.isRequired,
  geojsonLayers: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  deployConfig: PropTypes.object.isRequired,
  layer: PropTypes.object,
  setIpLayer: PropTypes.func,
  unfilteredCount: PropTypes.number.isRequired,
  thematicLayers: PropTypes.array.isRequired,
  isFree: PropTypes.bool.isRequired,
  isBasic: PropTypes.bool.isRequired
};

export default withTracker(() => {
  const layersSub = Meteor.subscribe('layers');

  return {
    loading: !layersSub.ready(),
    unfilteredCount: Layers.find({ deleted: { $eq: 'no' } }).count()
  };
})(LayerEditor);
